// RepoLayout.tsx — the shell every repository page is drawn inside.
//
// One header naming the repository (its health and score, plus archived or a
// visibility other than public when that is so: three facts at most) and
// one tab bar across the top, so Code, Pull requests, Automation, Agents,
// Activity and Settings are one click apart from wherever the reader is. The
// repository used to be navigated from a block at the bottom of the global left
// navigation, which had no counts and offered Settings to everyone.
//
// The bar is the shared `TabBar`: a `<nav aria-label="Repository">` of links,
// the current one carrying `aria-current="page"`, arrow keys moving between
// them. The pull request page is navigated by the same component.
//
// The content takes the whole width. The family is the header's family
// control and the Repositories list, not a rail repeated on every repository
// page; the default branch is the branch picker's, the role and the mirror
// are Settings' to say.

import { AlertTriangle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { JankuraiScoreBadge } from './JankuraiScoreBadge';
import { RepoDescription } from './RepoDescription';
import { RepoArchivedBadge } from './RepoArchivedBadge';
import { RepoHealthPill } from './RepoHealthPill';
import { TabBar } from './TabBar';
import { PageHeader, queryFreshness } from '../pageHeader';
import { PermissionDeniedState } from '../state';
import { livePresenceCount } from '../../api/agentPresence';
import { useRepoAgentPresence } from '../../hooks/useAgentPresence';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useAuth } from '../../hooks/useAuth';
import { useRepoAutomation } from '../../hooks/useRepoAutomation';
import { useRefs } from '../../hooks/useRefs';
import { useResolveRepo } from '../../hooks/useResolveRepo';
import { QUALITY_GATE_PATH } from '../../pages/qualityGate/qualityGateModel';
import { healthOpensChecks, repoFrontPath, repoShellCrumbs } from '../../pages/repoBrowserModel';
import { ClonePopover } from '../../pages/repositoryOverviewParts';
import {
  RepoHealthChecks,
  RepoHealthChip,
} from '../../pages/repositoryOverviewFacts';
import {
  canSeeRepoSettings,
  repoTabCount,
  repoTabHref,
  visibleRepoTabs,
  type RepoTabKey,
} from '../../pages/repoShellModel';

import '../browser/browser.css';
import './repoShell.css';

export interface RepoLayoutProps {
  provider: string;
  fullName: string;
  /** Which tab the page inside is. */
  tab: RepoTabKey;
  children: ReactNode;
}

export function RepoLayout({
  provider,
  fullName,
  tab,
  children,
}: RepoLayoutProps): JSX.Element {
  const resolved = useResolveRepo(provider, fullName);
  const summary = resolved.data?.summary ?? null;
  const repoId = resolved.data?.id ?? null;
  const { user } = useAuth();
  const bootstrap = useBootstrap();
  const permissions = bootstrap.data?.viewer.global_permissions ?? [];
  const mayOpenSettings = canSeeRepoSettings(user?.role, permissions);
  // The header's health chip stands for failing checks; pressing it lists them.
  const [checksOpen, setChecksOpen] = useState(false);

  const base = repoFrontPath(provider, fullName);
  const automation = useRepoAutomation(repoId);
  const refs = useRefs(repoId);
  // The Agents count: the live agents the presence list holds, once the Agents
  // tab has read it (other tabs only read the cache, so it costs no request),
  // else the repository summary's own count.
  const presence = useRepoAgentPresence(repoId, { fetch: tab === 'agents' });
  const activeAgents = presence.data
    ? livePresenceCount(presence.data.items)
    : (summary?.active_agents ?? null);
  const defaultBranch = summary?.default_branch ?? '';
  const defaultBranchSha =
    refs.data?.find((ref) => ref.kind === 'branch' && ref.name === defaultBranch)?.sha ?? null;
  const scorePath =
    summary && defaultBranchSha
      ? `/quality-gate/heads/${encodeURIComponent(summary.id.owner)}/${encodeURIComponent(
          summary.id.name
        )}/${encodeURIComponent(defaultBranchSha)}`
      : QUALITY_GATE_PATH;

  return (
    <div className="repo-shell" data-testid="repo-shell">
      {/* The shell's header is the page header of every repository route: the
          trail from Repositories down, the repository's name as the page's one
          h1, and how old the repository read is. The page inside the shell
          heads its own part with an h2. */}
      <PageHeader
        title={<span data-testid="repo-shell-title">{nameOf(fullName)}</span>}
        className="repo-shell__header"
        testId="repo-shell-header"
        parent="nav.repos"
        crumbs={repoShellCrumbs(provider, fullName, summary)}
        freshness={queryFreshness(resolved)}
        subtitle={summary?.description ? <RepoDescription text={summary.description} /> : undefined}
        actions={
          summary ? (
            <>
              <ClonePopover
                httpUrl={summary.clone_http_url}
                sshUrl={summary.clone_ssh_url}
              />
            </>
          ) : null
        }
        badges={
          summary ? (
            <>
              {healthOpensChecks(summary) ? (
                <RepoHealthChip
                  repo={summary}
                  open={checksOpen}
                  onToggle={() => setChecksOpen((v) => !v)}
                />
              ) : (
                <RepoHealthPill health={summary.health} />
              )}
              {/* The score links to this repository's scored head, as it does
                  in the table. */}
              <Link
                to={scorePath}
                className="repo-overview__score-link"
                title="See what produced this score"
                data-testid="repo-overview-score"
              >
                <JankuraiScoreBadge
                  score={summary.jankurai_score}
                  decision={summary.jankurai_decision}
                  scoredAt={summary.jankurai_scored_at}
                />
              </Link>
              {summary.archived ? (
                <RepoArchivedBadge archived={summary.archived} />
              ) : summary.visibility !== 'public' ? (
                <span className="page__pill" data-testid="repo-header-visibility">
                  {summary.visibility}
                </span>
              ) : null}
            </>
          ) : null
        }
      >
        {summary && checksOpen && healthOpensChecks(summary) ? (
          <RepoHealthChecks repo={summary} />
        ) : null}
        {summary?.archived ? <RepoArchivedNotice base={base} /> : null}
        <RepoTabs
          base={base}
          current={tab}
          mayOpenSettings={mayOpenSettings}
          openPullRequests={summary?.open_pull_requests ?? null}
          activeAgents={activeAgents}
          automationWarnings={automation.data?.warnings.length ?? 0}
        />
      </PageHeader>

      <div className="repo-shell__body">
        <div className="repo-shell__content">
          {tab === 'settings' && !mayOpenSettings ? (
            <PermissionDeniedState
              description="Only a repository administrator or a writer can open these settings."
              missingPermission="repo.admin"
            />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}

function RepoArchivedNotice({ base }: { base: string }): JSX.Element {
  return (
    <div className="repo-archived-notice" role="status" data-testid="repo-archived-notice">
      Archived: read-only.{' '}
      <Link to={`${base}/settings/danger-zone`}>
        Unarchive in Settings › Danger zone
      </Link>
    </div>
  );
}

function nameOf(fullName: string): string {
  return fullName.split('/').slice(-1)[0] ?? fullName;
}

function RepoTabs({
  base,
  current,
  mayOpenSettings,
  openPullRequests,
  activeAgents,
  automationWarnings,
}: {
  base: string;
  current: RepoTabKey;
  mayOpenSettings: boolean;
  openPullRequests: number | null;
  activeAgents: number | null;
  automationWarnings: number;
}): JSX.Element {
  const tabs = visibleRepoTabs(mayOpenSettings);
  // Automation is marked when something it reports needs a person; the query is
  // the same one the Automation tab reads, so the mark costs no extra request.
  const warned = automationWarnings > 0;

  const items = tabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
    href: repoTabHref(base, tab),
    count: repoTabCount(tab.key, { openPullRequests, activeAgents }),
    marker:
      tab.key === 'automation' && warned ? (
        <span
          className="repo-tabs__warning"
          data-testid="repo-tab-automation-warning"
        >
          <AlertTriangle size={14} aria-hidden="true" />
          <span className="sr-only">needs a person</span>
        </span>
      ) : undefined,
  }));

  return (
    <TabBar
      label="Repository"
      items={items}
      current={current}
      testId="repo-tabs"
      idPrefix="repo-tab"
    />
  );
}
